/**
 * EVERY COMPANY DOCUMENT, AND HOW ONE IS OPENED.
 *
 * The founder, 14 September:
 *
 *   "we should probably add one last card to the Overview screen of the
 *    Portal: Resources. That's where every single company document can be
 *    found - SOPs, departmental Welcome Packs, HR resources, processes and
 *    procedures, etc."
 *
 *   "and then obviously remove the reference to these documents that appears
 *    at the top of each card's screen."
 *
 * What this file used to be was the card shelf: `fetchCardDocuments(slug)`,
 * three documents, drawn above every card's permissions. That shelf is gone
 * from the card screens on the second instruction, and the documents did not
 * go with it -- they moved to one place that holds all of them.
 *
 * WHY THE KINDS ARE NOT LISTED HERE ANY MORE
 *
 * They were: a `DOCUMENT_KINDS` constant naming three, with their labels and
 * blurbs, beside a database CHECK constraint naming the same three. Script 330
 * turned that constraint into a registry table, so the list is now read at
 * render time -- which is the failure
 * `every_value_the_apps_offer_is_one_the_database_accepts.sh` exists for.
 * Script 312 offered four authentication methods and the CHECK accepted four
 * DIFFERENT ones; every press was refused and nothing on either side could see
 * it. A list read from the thing that enforces it cannot disagree with itself.
 *
 * WHY DOWNLOADS ARE SIGNED AND NOT LINKED
 *
 * The bucket is private, and it has to be: an SOP describes how Orchid detects
 * fraud, escalates disputes and screens documents, so a public URL would
 * publish the control to the people it controls for. A signed URL is minted per
 * click, expires, and is only issued to a session the storage policy already
 * accepts -- which means the link cannot be forwarded to somebody outside and
 * keep working.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const NOT_CONFIGURED = 'This portal cannot reach the database.';
const fail = (error) => ({ data: null, error });

const BUCKET = 'portal-documents';

/** How long a download link lives. Long enough to click, short enough to leak badly. */
const SIGNED_URL_SECONDS = 120;

/**
 * The same sentence for the same cause, wherever it happens.
 *
 * PGRST202 is PostgREST saying the function is not in its schema cache, which
 * in practice means the migration that creates it has not been run. The raw
 * message -- "Could not find the function public.fn_portal_resources() in the
 * schema cache" -- reads like a bug in this screen and sends whoever sees it
 * looking in the wrong place.
 */
const readable = (error, script, fallback) => {
  if ((error.code || '') === 'PGRST202') {
    return `Resources is not available on this database yet. If you are the `
         + `administrator, script ${script} has not been run.`;
  }
  return error.message || fallback;
};

/**
 * Every company document that applies now.
 *
 * ONE READ, NOT ONE PER DEPARTMENT. The rows come back carrying their card's
 * title, so the screen can group by department without a list of fourteen
 * department names written down in JavaScript -- the drift script 48 exists to
 * prevent.
 *
 * Superseded versions are absent by construction. Whoever needs the history
 * asks for it by document; whoever is doing their job needs the one in force.
 */
export async function fetchPortalResources() {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);

  const { data, error } = await supabase.rpc('fn_portal_resources');
  if (error) return fail(readable(error, 330, 'The company documents could not be read.'));
  return { data: data || [], error: null };
}

/**
 * What kinds of document exist, from the table that enforces it.
 *
 * `is_slot` is the distinction the screen draws on: a slot kind is promised
 * once per card, so its ABSENCE is named; a library kind is promised nowhere,
 * so there is nothing to be missing.
 */
export async function fetchDocumentKinds() {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);

  const { data, error } = await supabase.rpc('fn_portal_document_kinds');
  if (error) return fail(readable(error, 330, 'The document categories could not be read.'));
  return { data: data || [], error: null };
}

/**
 * Mint a link for one document, at the moment it is asked for.
 *
 * Not on render: signing every document on the screen would issue links nobody
 * clicks, and each one is a credential with a lifetime.
 */
export async function downloadUrlFor(storagePath) {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  if (!storagePath) return fail('That document has no file behind it.');

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_SECONDS);

  if (error) {
    if (/not found|does not exist/i.test(error.message || '')) {
      return fail('The file for this document is missing from storage. It was recorded but never uploaded.');
    }
    return fail(error.message || 'That download could not be prepared.');
  }
  return { data: data?.signedUrl || null, error: null };
}

/**
 * Put a file in the bucket and record it.
 *
 * TWO STEPS AND NOT ONE, because they are two different systems and only the
 * second is transactional. The object goes up first: a row pointing at a file
 * that was never uploaded renders as a document whose download always fails,
 * which is a worse state than a file nobody has a row for.
 *
 * `upsert: false` on purpose. The storage policy grants INSERT and nothing
 * else -- "a superseded SOP has to stay readable. Replacing the OBJECT while
 * the row still points at it would make the version history a lie" (script 59)
 * -- so the path carries a timestamp and each version is its own object.
 */
export async function publishDocument({
  file, kind, cardSlug, title, summary, supersedes,
}) {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  if (!file) return fail('Choose a file to publish.');
  if (!String(title || '').trim()) return fail('Give the document a title.');
  if (!kind) return fail('Say what kind of document this is.');

  // The path a person would recognise, under the scope it belongs to, with the
  // moment it was published -- so two versions never collide and the bucket
  // reads as a filing cabinet rather than a pile of uuids.
  const safe = String(file.name || 'document')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(-80) || 'document';
  const path = `${cardSlug || 'company'}/${kind}/${Date.now()}-${safe}`;

  const up = await supabase.storage.from(BUCKET).upload(path, file, {
    upsert: false,
    contentType: file.type || undefined,
  });
  if (up.error) {
    if (/row-level security|not authorized|violates/i.test(up.error.message || '')) {
      return fail('Storage refused the upload. Publishing a document needs the '
                + 'permission that manages staff accounts.');
    }
    return fail(up.error.message || 'That file could not be uploaded.');
  }

  const { data, error } = await supabase.rpc('fn_publish_portal_document', {
    p_kind: kind,
    p_card_slug: cardSlug || null,
    p_title: String(title).trim(),
    p_storage_path: path,
    p_summary: summary ? String(summary).trim() : null,
    p_content_type: file.type || null,
    p_size_bytes: typeof file.size === 'number' ? file.size : null,
    p_supersedes: supersedes || null,
  });

  if (error) return fail(readable(error, 330, 'That document could not be published.'));
  const row = Array.isArray(data) ? data[0] : data;
  return { data: row || null, error: null };
}

/**
 * The documents, grouped the way the screen draws them.
 *
 * COMPANY-WIDE FIRST, then one group per department, IN THE ORDER THE DATABASE
 * RETURNED THEM. `fn_portal_resources` has already applied the catalogue's
 * sort_order and the registry's; re-sorting here would throw away two
 * decisions somebody made and replace them with the alphabet.
 */
export function groupResources(rows = []) {
  const groups = [];
  const index = new Map();

  (rows || []).forEach((row) => {
    const key = row.card_slug || '__company__';
    if (!index.has(key)) {
      const group = {
        key,
        slug: row.card_slug || null,
        // A card whose title did not come back has been deleted from the
        // catalogue while its documents stayed. Named rather than blank.
        title: row.card_slug ? (row.card_title || row.card_slug) : 'Company-wide',
        documents: [],
      };
      index.set(key, group);
      groups.push(group);
    }
    index.get(key).documents.push(row);
  });

  return groups;
}

/**
 * Which SLOTS a department has left empty.
 *
 * A MISSING DOCUMENT IS NAMED RATHER THAN OMITTED. Two documents on a shelf
 * look complete until somebody goes hunting for the third; the whole value of
 * promising an SOP behind every card is that its ABSENCE is visible to the
 * person who needs it, not only to whoever was meant to write it.
 *
 * LIBRARY KINDS ARE NOT COUNTED, and that is the distinction script 330 put in
 * the database rather than here. Nobody promised Finance an HR resource, so
 * Finance is not missing one -- listing every library kind as absent on every
 * department would turn a real gap into fourteen rows of noise and the real
 * gap would stop being visible, which is the failure this behaviour exists to
 * prevent.
 */
export function missingSlots(kinds = [], documents = [], { companyWide = false } = {}) {
  const present = new Set((documents || []).map((d) => d.kind));
  return (kinds || [])
    .filter((k) => k.is_slot)
    // The company pack belongs to no department and every department's shelf
    // would otherwise report it missing.
    .filter((k) => (companyWide
      ? k.kind === 'company_welcome_pack'
      : k.kind !== 'company_welcome_pack'))
    .filter((k) => !present.has(k.kind));
}
